import React, { FC, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './platform-stats-payments.scss';

interface PaymentMetrics {
  rangeDays: number;
  since: string;
  rollout: { flagSet: boolean; cohortSize: number };
  requests: {
    total: number;
    byState: Array<{ state: string; count: number }>;
    byReasonCode: Array<{ reasonCode: string; count: number }>;
  };
  outcomes: {
    paid: number;
    declined: number;
    unknown: number;
    cannotContinue: number;
    expired: number;
    cancelled: number;
    live: number;
  };
  judge: { matches: number; mismatch: number; insufficientEvidence: number };
  approvals: {
    pending: number;
    approved: number;
    rejected: number;
    expired: number;
    voided: number;
    consumed: number;
    medianSecondsToDecision: number | null;
  };
  waits: {
    approval: number;
    cvv: number;
    threeDs: number;
    threeDsLive: number;
  };
  attempts: {
    briefs: number;
    single: number;
    second: number;
    thirdOrMore: number;
    max: number;
  };
  contracts: {
    byContract: Array<{ contractId: string; count: number }>;
    totalSource: { exact: number; heuristic: number; unknown: number };
    merchantStoredCards: number;
    byInstrumentType: Array<{ instrumentType: string; count: number }>;
  };
}

interface MetricsResponse {
  success: boolean;
  metrics: PaymentMetrics;
}

const DAY_OPTIONS = [7, 30, 90, 365];

const errorMessage = (err: unknown, fallback: string): string => {
  const axiosErr = err as { response?: { data?: { error?: string } } };
  return axiosErr?.response?.data?.error || fallback;
};

const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return '—';
  if (seconds < 90) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${Math.round(minutes)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
};

const percent = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

const Tile: FC<{
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'green' | 'yellow' | 'red';
}> = ({ label, value, sub, tone }) => (
  <div className='summary-tile'>
    <span className='tile-label'>
      {tone && <span className={`dot ${tone}`} />}
      {label}
    </span>
    <span className='tile-value'>{value}</span>
    {sub && <span className='tile-sub'>{sub}</span>}
  </div>
);

const CountTable: FC<{
  title: string;
  hint: string;
  keyLabel: string;
  rows: Array<{ key: string; count: number }>;
  total: number;
}> = ({ title, hint, keyLabel, rows, total }) => (
  <div className='payments-card'>
    <h3>{title}</h3>
    <p className='card-hint'>{hint}</p>
    {rows.length === 0 ? (
      <p className='payments-empty'>Nothing in this window.</p>
    ) : (
      <table className='payments-table'>
        <thead>
          <tr>
            <th>{keyLabel}</th>
            <th className='num'>Count</th>
            <th className='num'>Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <code>{row.key}</code>
              </td>
              <td className='num'>{row.count}</td>
              <td className='num'>{percent(row.count, total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

/**
 * Admin "Payments" tab: sanitized aggregates for the secure-payment canary
 * (GET /platform-stats/payments). Only counts, states, reason codes and
 * contract ids are shown — the endpoint never returns merchants, amounts,
 * users, threads or card data, so nothing here can identify a purchase.
 */
const PlatformStatsPayments: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [days, setDays] = useState(30);
  const [metrics, setMetrics] = useState<PaymentMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMetrics(null);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<MetricsResponse>('/platform-stats/payments', {
          params: { days },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.data.success) setMetrics(res.data.metrics);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load payment stats'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, days]);

  const m = metrics;
  // "consumed" is an approval the executor already spent, so it counts as approved.
  const approvedTotal = m ? m.approvals.approved + m.approvals.consumed : 0;
  const decisions = m ? approvedTotal + m.approvals.rejected : 0;

  return (
    <div className='payments-stats'>
      <div className='payments-toolbar'>
        <div className='day-selector'>
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              className={`day-btn ${days === d ? 'active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d === 365 ? '1 year' : `${d} days`}
            </button>
          ))}
        </div>
        {m && (
          <div className='payments-rollout'>
            <span className={`dot ${m.rollout.flagSet ? 'green' : 'red'}`} />
            {m.rollout.flagSet
              ? 'Secure payments enabled'
              : 'Secure payments disabled'}
            {' · '}
            {m.rollout.cohortSize} in canary cohort
          </div>
        )}
      </div>

      {error && <div className='payments-error'>{error}</div>}
      {loading && !m && <div className='loading'>Loading payment stats...</div>}

      {m && (
        <>
          <div className='payments-tiles'>
            <Tile
              label='Payment steps'
              value={m.requests.total}
              sub={`last ${m.rangeDays} days`}
            />
            <Tile
              label='Paid'
              value={m.outcomes.paid}
              sub={percent(m.outcomes.paid, m.requests.total)}
              tone='green'
            />
            <Tile label='Declined' value={m.outcomes.declined} tone='red' />
            <Tile
              label='Unknown outcome'
              value={m.outcomes.unknown}
              sub='needs a manual check'
              tone='yellow'
            />
            <Tile
              label='Stopped safely'
              value={m.outcomes.cannotContinue}
              sub='cannot_continue'
            />
            <Tile label='Expired' value={m.outcomes.expired} />
            <Tile label='Cancelled' value={m.outcomes.cancelled} />
            <Tile
              label='In flight'
              value={m.outcomes.live}
              sub='waiting or running now'
            />
          </div>

          <div className='payments-tiles'>
            <Tile
              label='Judge: matched'
              value={m.judge.matches}
              sub='approval card shown'
              tone='green'
            />
            <Tile
              label='Judge: mismatch'
              value={m.judge.mismatch}
              sub='brief ≠ checkout'
              tone='red'
            />
            <Tile
              label='Judge: not enough evidence'
              value={m.judge.insufficientEvidence}
              tone='yellow'
            />
            <Tile
              label='Approved'
              value={approvedTotal}
              sub={`${percent(approvedTotal, decisions)} of decisions`}
              tone='green'
            />
            <Tile label='Rejected' value={m.approvals.rejected} tone='red' />
            <Tile
              label='Approval expired'
              value={m.approvals.expired}
              sub='no answer in time'
            />
            <Tile
              label='Voided'
              value={m.approvals.voided}
              sub='checkout changed / crash'
            />
            <Tile
              label='Median time to decide'
              value={formatDuration(m.approvals.medianSecondsToDecision)}
            />
          </div>

          <div className='payments-tiles'>
            <Tile label='Waited for approval' value={m.waits.approval} />
            <Tile label='Asked for CVV' value={m.waits.cvv} />
            <Tile label='Asked for 3DS code' value={m.waits.threeDs} />
            <Tile
              label='Live bank challenge'
              value={m.waits.threeDsLive}
              sub='owner finished it in the live view'
            />
            <Tile
              label='Briefs used'
              value={m.attempts.briefs}
              sub={`${m.attempts.single} once · ${m.attempts.second} twice · ${m.attempts.thirdOrMore} 3+`}
            />
            <Tile
              label='Most attempts on one brief'
              value={m.attempts.max}
              tone={m.attempts.max >= 3 ? 'yellow' : undefined}
            />
            <Tile
              label='Total read exactly'
              value={m.contracts.totalSource.exact}
              sub={`${m.contracts.totalSource.heuristic} guessed · ${m.contracts.totalSource.unknown} unknown`}
            />
            <Tile
              label='Card saved at the shop'
              value={m.contracts.merchantStoredCards}
              sub='selected in the shop UI'
            />
          </div>

          <div className='payments-grid'>
            <CountTable
              title='Request states'
              hint='Where each payment step ended up (or is right now).'
              keyLabel='State'
              rows={m.requests.byState.map((r) => ({
                key: r.state,
                count: r.count,
              }))}
              total={m.requests.total}
            />
            <CountTable
              title='Reason codes'
              hint='Why steps stopped. These are the same codes the agent receives.'
              keyLabel='Reason'
              rows={m.requests.byReasonCode.map((r) => ({
                key: r.reasonCode,
                count: r.count,
              }))}
              total={m.requests.total}
            />
            <CountTable
              title='Checkout contracts'
              hint='Which contract matched the checkout for each approval card.'
              keyLabel='Contract'
              rows={m.contracts.byContract.map((r) => ({
                key: r.contractId,
                count: r.count,
              }))}
              total={m.judge.matches}
            />
            <CountTable
              title='Payment instruments'
              hint='Instrument type chosen on the approval card (never the card itself).'
              keyLabel='Instrument'
              rows={m.contracts.byInstrumentType.map((r) => ({
                key: r.instrumentType,
                count: r.count,
              }))}
              total={m.judge.matches}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PlatformStatsPayments;
